import type { ReactNode } from "react";
import { Route, useParams } from "react-router-dom";

// ---------------------------------------------------------------------------
// Type-safe path param extraction
// ---------------------------------------------------------------------------

type PathSegmentParams<Segment extends string> =
  Segment extends `:${infer Param}`
    ? { [K in Param]: string }
    : Segment extends "*"
      ? { "*": string }
      : Record<never, never>;

type MergeParams<A, B> = {
  [K in keyof A | keyof B]: K extends keyof B
    ? B[K]
    : K extends keyof A
      ? A[K]
      : never;
};

export type RoutePathParams<Path extends string> =
  Path extends `${infer Head}/${infer Tail}`
    ? MergeParams<PathSegmentParams<Head>, RoutePathParams<Tail>>
    : PathSegmentParams<Path>;

type RouteOptions = {
  exact?: boolean;
};

type StaticRoutePath<Path extends string> =
  keyof RoutePathParams<Path> extends never ? Path : never;

type DynamicRoutePath<Path extends string> =
  keyof RoutePathParams<Path> extends never ? never : Path;

// ---------------------------------------------------------------------------
// RouteMatch — isolates useParams so children receive typed props
// ---------------------------------------------------------------------------

function RouteMatch<Path extends string>({
  children,
}: {
  children: (params: RoutePathParams<Path>) => ReactNode;
}) {
  const params = useParams<RoutePathParams<Path>>();
  return <>{children(params)}</>;
}

// ---------------------------------------------------------------------------
// matchRoute — React Router v5 adapter
// ---------------------------------------------------------------------------

export function matchRoute<Path extends string>(
  routePath: DynamicRoutePath<Path>,
  render: (params: RoutePathParams<Path>) => ReactNode,
  options?: RouteOptions
) {
  return (
    <Route path={routePath} exact={options?.exact}>
      <RouteMatch<Path>>{render}</RouteMatch>
    </Route>
  );
}

export function renderRoute<Path extends string>(
  routePath: StaticRoutePath<Path>,
  render: () => ReactNode,
  options?: RouteOptions
) {
  return (
    <Route path={routePath} exact={options?.exact}>
      {render()}
    </Route>
  );
}
